import { ReactNode, useState } from "react";

export interface Tab {
    id: string;
    label: string;
    content: ReactNode;
}

interface TabsProps {
    tabs: Tab[];
    defaultTabId?: string;
    activeTabId?: string;
    onTabChange?: (tabId: string) => void;
    className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
    tabs,
    defaultTabId,
    activeTabId: controlledActiveTabId,
    onTabChange,
    className = "",
}) => {
    const [internalActiveTabId, setInternalActiveTabId] = useState(defaultTabId || tabs[0]?.id);

    // Use controlled value if provided, otherwise use internal state
    const activeTabId = controlledActiveTabId !== undefined ? controlledActiveTabId : internalActiveTabId;

    const handleTabChange = (tabId: string) => {
        if (controlledActiveTabId === undefined) {
            setInternalActiveTabId(tabId);
        }
        onTabChange?.(tabId);
    };

    const activeTab = tabs.find((tab) => tab.id === activeTabId);

    return (
        <div className={className}>
            {/* Mobile Select Dropdown */}
            <div className="sm:hidden">
                <label htmlFor="tab-select" className="sr-only">
                    Select a tab
                </label>
                <select
                    id="tab-select"
                    className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    value={activeTabId}
                    onChange={(e) => handleTabChange(e.target.value)}
                >
                    {tabs.map((tab) => (
                        <option key={tab.id} value={tab.id}>
                            {tab.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Desktop Tab Navigation */}
            <div className="hidden sm:block border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`
                                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                ${
                                    activeTabId === tab.id
                                        ? "border-blue-500 text-blue-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }
                            `}
                            aria-current={activeTabId === tab.id ? "page" : undefined}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">{activeTab?.content}</div>
        </div>
    );
};
